import type { CSSProperties } from 'react';
import { currency, decimal } from '@/shared/lib/format';
import { passbookCurrency } from '@/shared/lib/passbook-utils';
import { movementProgram } from '@/features/leads/model/lead-logs';
import type { Passbook } from '@/shared/types';
import type { ParticipantTier } from '../api/participant-api';

/**
 * Texto de progresso no vocabulário da loja ("Faltam 180 pontos para Prestige").
 * No topo da escada: "Você está no nível máximo".
 */
export function formatTierProgress(tier: ParticipantTier, passbook: Passbook): string {
  if (!tier.next) return 'Você está no nível máximo';
  const remaining = tier.next.remaining;
  const program = movementProgram(passbook);
  let amount: string;
  if (program === 'cashback' || program === 'balance') {
    amount = currency(remaining, passbookCurrency(passbook));
  } else if (program === 'points') {
    amount = `${decimal(remaining, Number.isInteger(remaining) ? 0 : 2)} ${remaining === 1 ? 'ponto' : 'pontos'}`;
  } else {
    const stamps = Math.round(remaining);
    amount = `${stamps} ${stamps === 1 ? 'selo' : 'selos'}`;
  }
  const verb = remaining === 1 ? 'Falta' : 'Faltam';
  return `${verb} ${amount} para ${tier.next.label}`;
}

function asTrimmed(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text || undefined;
}

function asFinite(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Mesma regra do BFF (`server/lib/participant-area.ts` `resolveParticipantTier`).
 * Usada no mock da área do participante — o `/me` real resolve no servidor.
 */
export function resolveParticipantTier(
  passbook: Pick<Passbook, 'hasTiers' | 'tiers'>,
  user: { tierOverride?: { level?: string } | null } | null | undefined,
  balance: number,
): ParticipantTier | null {
  if (!passbook.hasTiers || !passbook.tiers?.length) return null;

  const tiers = passbook.tiers
    .map((t) => ({
      level: asTrimmed(t.level) ?? '',
      label: asTrimmed(t.label) ?? '',
      threshold: asFinite(t.threshold) ?? 0,
      order: asFinite(t.order) ?? 0,
      color: asTrimmed(t.color),
      contentBenefits: asTrimmed(t.contentBenefits),
      backgroundColor: asTrimmed(t.backgroundColor),
      foregroundColor: asTrimmed(t.foregroundColor),
      labelColor: asTrimmed(t.labelColor),
    }))
    .filter((t) => t.level && t.label);
  if (!tiers.length) return null;

  const byDesc = [...tiers].sort((a, b) => b.threshold - a.threshold);
  const byAsc = [...tiers].sort((a, b) => a.threshold - b.threshold || a.order - b.order);
  const points = Number(balance) || 0;
  const forced = asTrimmed(user?.tierOverride?.level);
  const match =
    (forced ? tiers.find((t) => t.level === forced) : undefined) ??
    byDesc.find((t) => points >= t.threshold) ??
    byAsc[0];
  if (!match) return null;

  const rank = byAsc.findIndex((t) => t.level === match.level) + 1;
  const next = byAsc.find((t) => t.threshold > match.threshold);
  const span = next ? next.threshold - match.threshold : 0;
  const progress = next ? (span <= 0 ? 1 : Math.min(1, Math.max(0, (points - match.threshold) / span))) : 1;

  return {
    level: match.level,
    label: match.label,
    color: match.color,
    backgroundColor: match.backgroundColor,
    foregroundColor: match.foregroundColor,
    labelColor: match.labelColor,
    benefits: match.contentBenefits,
    rank,
    count: tiers.length,
    progress,
    next: next ? { label: next.label, remaining: Math.max(0, next.threshold - points) } : null,
  };
}

function parseCssRgb(color: string): [number, number, number] | null {
  const trimmed = color.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(trimmed);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i.exec(trimmed);
  if (!rgb) return null;
  return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
}

function luminance(rgb: [number, number, number]): number {
  const linear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return linear(rgb[0]) * 0.2126 + linear(rgb[1]) * 0.7152 + linear(rgb[2]) * 0.0722;
}

/**
 * Mantém o tom do passbook, mas clareia no tema escuro / escurece no claro
 * até o texto do nível ler no card do tema (Ambassadeur #1c1917 no dark, cream no light).
 */
export function readableOnTheme(color: string | undefined, theme: 'light' | 'dark'): string | undefined {
  if (!color) return undefined;
  const rgb = parseCssRgb(color);
  if (!rgb) return color;
  const minL = theme === 'dark' ? 0.62 : undefined;
  const maxL = theme === 'light' ? 0.45 : undefined;
  let [r, g, b] = rgb;
  let l = luminance(rgb);
  const toward = theme === 'dark' ? [255, 255, 255] : [0, 0, 0];
  for (let i = 0; i < 16 && ((minL != null && l < minL) || (maxL != null && l > maxL)); i++) {
    r += (toward[0] - r) * 0.18;
    g += (toward[1] - g) * 0.18;
    b += (toward[2] - b) * 0.18;
    l = luminance([r, g, b]);
  }
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/** Texto/borda do nível: uma cor por tema, via `.dark`. */
export function tierTextStyle(color: string | undefined): CSSProperties | undefined {
  const light = readableOnTheme(color, 'light');
  const dark = readableOnTheme(color, 'dark');
  if (!light && !dark) return undefined;
  return {
    '--tier-text': light ?? dark ?? '',
    '--tier-text-dark': dark ?? light ?? '',
  } as CSSProperties;
}

export const TIER_TEXT_CLASS = '[color:var(--tier-text)] dark:[color:var(--tier-text-dark)]';
